/*
 * Modal de registro de jornada: muestra StartTimeRecordForm o EndTimeRecordForm
 * segun si hay un worklog pendiente. Valida autorizacion de horas extra si >= 8h.
 */
import React, { useEffect, useState } from 'react'
import { Modal, Spin, Alert } from 'antd'
import { useTranslation } from 'react-i18next'
import StartTimeRecordForm from '../forms/startTimeRecordForm'
import EndTimeRecordForm from '../forms/endTimeRecordForm'
import {
  SortOrder,
  Worklog,
  Worklog_Authorization,
  Worklog_AuthorizationOrderByInput,
  Worklog_AuthorizationWhereInput,
  WorklogOrderByInput,
  WorklogWhereInput
} from '../../../generated/graphql'
import { getPayload } from '../../../utils/auth'
import { useLazyQuery } from '@apollo/client'
import { WORKLOGS, WORKLOGS_AUTORIZATION } from '../gql/verteOfficeGQL'

interface TimeRecordModalProps {
  initialOpen?: boolean
  onClose?: () => void
}

interface WorklogOutputType {
  worklogs: Worklog[]
}

interface WorklogInputType {
  where: WorklogWhereInput
  orderBy: WorklogOrderByInput[]
}

type WorklogAutorizationOutputType = {
  worklogAuthorizations: Worklog_Authorization[]
}

type WorklogAutorizationInputType = {
  where: Worklog_AuthorizationWhereInput
  orderBy: Worklog_AuthorizationOrderByInput[]
}

// Retorna inicio y fin del dia actual en UTC (para filtrar worklogs de hoy)
const getTodayUtcRange = () => {
  const now = new Date()

  const startOfDayUTC = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0)
  )

  const endOfDayUTC = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      23,
      59,
      59,
      999
    )
  )

  return {
    start: startOfDayUTC.toISOString(),
    end: endOfDayUTC.toISOString()
  }
}

export const WORKLOG_ID_KEY = 'current_worklog_id'

const TimeRecordModal: React.FC<TimeRecordModalProps> = ({
  initialOpen = true,
  onClose
}) => {
  const [getWorklogsById, { loading: loadingById, data: dataById }] =
    useLazyQuery<WorklogOutputType, WorklogInputType>(WORKLOGS, {
      fetchPolicy: 'no-cache'
    })

  const [getWorklogs, { loading: loadingAll, data: dataAll }] = useLazyQuery<
    WorklogOutputType,
    WorklogInputType
  >(WORKLOGS, {
    fetchPolicy: 'no-cache'
  })

  const [
    getWorklogAutorizations,
    { loading: autorizationLoading, data: authorizationData }
  ] = useLazyQuery<WorklogAutorizationOutputType, WorklogAutorizationInputType>(
    WORKLOGS_AUTORIZATION,
    {
      fetchPolicy: 'no-cache'
    }
  )

  const PAYLOAD = getPayload()
  const { t } = useTranslation()
  const [open, setOpen] = useState(initialOpen)

  const handleCancel = () => {
    setOpen(false)
    onClose?.()
  }

  useEffect(() => {
    try {
      if (PAYLOAD) {
        const worklogId = localStorage.getItem(WORKLOG_ID_KEY)

        if (worklogId) {
          getWorklogsById({
            variables: {
              where: {
                id: { equals: worklogId },
                user_id: { equals: PAYLOAD?.id },
                deleted_at: { equals: null }
              },
              orderBy: [{ created_at: SortOrder.Desc }]
            }
          })
        }

        const { start, end } = getTodayUtcRange()

        getWorklogs({
          variables: {
            where: {
              user_id: { equals: PAYLOAD?.id },
              deleted_at: { equals: null },
              start_time: { gte: start, lte: end }
            },
            orderBy: [{ created_at: SortOrder.Desc }]
          }
        })

        getWorklogAutorizations({
          variables: {
            where: {
              user_id: { equals: PAYLOAD?.id },
              deleted_at: { equals: null },
              date: { gte: start, lte: end }
            },
            orderBy: [{ created_at: SortOrder.Desc }]
          }
        })
      }
    } catch (error) {
      console.error('Error getting payload:', error)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchedWorklog = dataById?.worklogs?.[0]
  const isFinished = fetchedWorklog != null && fetchedWorklog.end_time != null
  if (isFinished) {
    localStorage.removeItem(WORKLOG_ID_KEY)
  }
  const pendingWorklog = isFinished ? undefined : fetchedWorklog
  const todayWorklogs = dataAll?.worklogs ?? []

  const totalTodayWorklogHours = todayWorklogs.reduce(
    (acc, worklog) => acc + (worklog.total_hours ?? 0),
    0
  )
  const todayAuthorizations = authorizationData?.worklogAuthorizations ?? []

  const totalAuthorizationHours = todayAuthorizations.reduce(
    (acc, auth) => acc + (auth.total_hours ?? 0),
    0
  )

  const renderContent = () => {
    if (loadingById || loadingAll || autorizationLoading) {
      return <Spin />
    }

    if (pendingWorklog != null) {
      return (
        <EndTimeRecordForm
          worklogId={pendingWorklog.id}
          startTime={pendingWorklog.start_time}
          onCancel={handleCancel}
          onClose={handleCancel}
        />
      )
    }

    if (totalTodayWorklogHours >= 8) {
      if (totalAuthorizationHours === 0) {
        return (
          <Alert
            message={t('no_overtime_authorization')}
            type='warning'
            showIcon
          />
        )
      }

      return (
        <StartTimeRecordForm onCancel={handleCancel} onClose={handleCancel} />
      )
    }

    return (
      <StartTimeRecordForm onCancel={handleCancel} onClose={handleCancel} />
    )
  }

  return (
    <Modal
      open={open}
      title={t('time_record')}
      onCancel={handleCancel}
      onOk={handleCancel}
      footer={null}
      destroyOnClose
    >
      {renderContent()}
    </Modal>
  )
}

export default TimeRecordModal
